import Section from 'app/frontend/base/components/Section'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import { Navigate, useParams } from 'react-router-dom'
import useReferenceStore from 'app/frontend/base/state/useReferenceStore'
import EventHeader from './components/EventHeader'
import {
  useEventActivitiesPlanningInfo,
  useTeamEvent,
} from 'app/frontend/base/config/queries'
import EventActivityBlock from './components/EventActivityBlock'
import { useCallback, useMemo, useState } from 'react'
import AddParticipantModal from './components/AddParticipantModal'
import AddEventActivitiesDialog from './components/AddEventActivitiesDialog'
import useApplyEventUpdate from './hooks/useApplyEventUpdate'
import useOptimisticEventUpdate from './hooks/useOptimisticEventUpdate'
import { AddEventFormValues } from '../TeamEventOverview/components/AddEventDialog'
import {
  TeamEventUpdateContextValue,
  TeamEventUpdateProvider,
} from './context/TeamEventUpdateContext'
import EventActivityGrid from './components/EventActivityGrid'
import EventSetupModeControl from './components/EventSetupModeControl'
import EventGetStartedBanner from './components/EventGetStartedBanner'
import EventActivitySpecificEncounter from './components/EventActivitySpecificEncounter'
import { resolveEventFocusActivity } from './components/eventFocusActivity'
import { eventActivitiesNoun } from './components/eventActivityNoun'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import {
  TeamEventParticipantsProvider,
  useTeamEventParticipantsContext,
} from './context/TeamEventParticipantsContext'
import {
  TeamEventPlanningInfoProvider,
  useTeamEventPlanningInfoState,
} from './context/TeamEventPlanningInfoContext'
import { Switch } from '@/components/ui/switch'
import Card from 'app/frontend/base/components/Card'
import EventAttendanceSummary from './components/EventAttendanceSummary'
import useTeamEventMutations from './mutations/useTeamEventMutations'
import ViewerSignupCard from './components/ViewerSignupCard'
import {
  CreateEventActivitiesPayload,
  UpdateEventActivityPayload,
  updateEventParticipantAdminComment,
  updateEventParticipantStatus,
} from 'app/frontend/base/api/event'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import ImportFromPreviousEventDialog from './components/ImportFromPreviousEventDialog'
import AutoSetupDialog from './components/AutoSetupDialog'
import AutoSetupPreviewBar from './components/AutoSetupPreviewBar'
import AutoSetupGeneratingOverlay from './components/AutoSetupGeneratingOverlay'
import EventBulkSelectionMenuItem from './components/EventBulkSelectionMenuItem'
import { Bot, Settings2Icon } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useEventActivityLinkedPlanData, {
  EventActivityLinkedPlanData,
} from './hooks/useEventActivityLinkedPlanData'
import { TeamEventLinkedVisualPlansProvider } from './context/TeamEventLinkedVisualPlansContext'
import {
  hasLinkedRaidPlanRefs,
  linkedPlanRevealClassName,
} from './components/linkedPlanReveal'
import { useCopyToClipboard } from 'app/frontend/base/hooks/useCopyToClipboard'
import { buildEventActivityLinkedPlanNote } from './components/eventActivityNoteExport'
import TeamEventSkeleton from './components/TeamEventSkeleton'
import EventNotes from './components/EventNotes'
import EventNotesControl from './components/EventNotesControl'
import { cn } from '@/lib/utils'
import { useStickyTopPx } from 'app/frontend/base/hooks/useStickyTop'
import useAutoSetupPreview from './hooks/useAutoSetupPreview'
import {
  applyActivityOrder,
  applyActivitySelectionForAll,
  applyActivityUpdate,
  applyAllActivitiesSelection,
  applyParticipantAdminComment,
  applyParticipantStatus,
  applySelection,
} from './hooks/eventEditReducers'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'

type TeamEventLinkedPlanDataProviderProps = {
  activities: wowaudit.EventActivity[]
  activityType: wowaudit.Event['activityType']
  children: (data: {
    linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>
    isRaidPlansLoading: boolean
  }) => React.ReactNode
}

const TeamEventLinkedPlanDataProvider = ({
  activities,
  activityType,
  children,
}: TeamEventLinkedPlanDataProviderProps) => {
  const { linkedPlanDataByActivityId, isRaidPlansLoading, visualPlansById } =
    useEventActivityLinkedPlanData({
      activities,
      enabled: activityType === 'raids',
    })
  return (
    <TeamEventLinkedVisualPlansProvider visualPlansById={visualPlansById}>
      {children({ linkedPlanDataByActivityId, isRaidPlansLoading })}
    </TeamEventLinkedVisualPlansProvider>
  )
}

type CopyAllNotesButtonProps = {
  activities: wowaudit.EventActivity[]
  linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>
  includeInactive?: boolean
  isRaidPlansLoading?: boolean
}

const CopyAllNotesButton = ({
  activities,
  linkedPlanDataByActivityId,
  includeInactive = false,
  isRaidPlansLoading = false,
}: CopyAllNotesButtonProps) => {
  const { team } = useTeam()
  const gameVersion = useGameVersion()
  const { allEntries } = useTeamEventParticipantsContext()
  const { copied, copyToClipboard } = useCopyToClipboard()
  const copiedAllNotes = copied !== undefined
  const noteActivities = includeInactive
    ? activities
    : activities.filter((activity) => activity.active)

  const handleCopyAllNotes = () => {
    const allNotes = noteActivities
      .map((activity) =>
        buildEventActivityLinkedPlanNote({
          activity,
          linkedPlanData: linkedPlanDataByActivityId.get(activity.id),
          allEntries,
          planAbilityDefaults: team.planAbilityDefaults,
        }),
      )
      .filter((note) => note.length > 0)
      .join('\n\n')
    if (!allNotes) return
    copyToClipboard(allNotes)
  }

  const hasLinkedPlanRefs =
    gameVersion.has('raid_plans') && noteActivities.some(hasLinkedRaidPlanRefs)
  const hasResolvedLinkedPlans =
    gameVersion.has('raid_plans') &&
    noteActivities.some(
      (activity) =>
        (linkedPlanDataByActivityId.get(activity.id)?.raidPlans.length ?? 0) >
        0,
    )
  const isRevealPending = isRaidPlansLoading && hasLinkedPlanRefs
  const hasLinkedPlans = isRevealPending
    ? hasLinkedPlanRefs
    : hasResolvedLinkedPlans

  if (hasLinkedPlans) {
    return (
      <Button
        size='sm'
        onClick={handleCopyAllNotes}
        className={cn(
          'flex flex-col h-auto py-1 leading-tight',
          linkedPlanRevealClassName(isRevealPending),
        )}
      >
        <span>{copiedAllNotes ? 'Copied all notes!' : 'Copy all notes'}</span>
        <span className='text-xs opacity-70'>and invites</span>
      </Button>
    )
  }

  return (
    <Button
      size='sm'
      onClick={handleCopyAllNotes}
      className={linkedPlanRevealClassName(isRevealPending)}
    >
      {copiedAllNotes ? 'Copied all invites!' : 'Copy all invites'}
    </Button>
  )
}

const TeamEvent = () => {
  const { team } = useTeam()
  const { eventId } = useParams()
  const parsedEventId = Number(eventId)
  const numericEventId = Number.isFinite(parsedEventId)
    ? parsedEventId
    : undefined

  if (!numericEventId) {
    return <Navigate to={`${team.path}/events`} replace />
  }

  return <TeamEventContent key={numericEventId} eventId={numericEventId} />
}

const TeamEventContent = ({ eventId }: { eventId: number }) => {
  const { t } = useTranslation()
  const { accessLevel } = useAuth()
  const { team, guild } = useTeam()
  const gameVersion = useGameVersion()
  const { getReference } = useReferenceStore()
  const [addParticipantsOpen, setAddParticipantsOpen] = useState(false)
  const [addActivitiesOpen, setAddActivitiesOpen] = useState(false)
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const [isAutoSetupOpen, setIsAutoSetupOpen] = useState(false)
  const [isResetSelectionsConfirmOpen, setIsResetSelectionsConfirmOpen] =
    useState(false)
  const [isEditingNotes, setIsEditingNotes] = useState(false)
  const [resetGridViewKey, setResetGridViewKey] = useState(0)
  const canAdminEdit = accessLevel === 'admin'
  const stickyTopPx = useStickyTopPx(true)
  const preview = useAutoSetupPreview({
    eventId,
    onRequestConfig: () => setIsAutoSetupOpen(true),
    onGenerateStart: () => setResetGridViewKey((key) => key + 1),
  })
  const {
    data: event,
    isLoading: isLoadingEvent,
    isError: hasEventError,
  } = useTeamEvent(team.id, eventId, undefined, !preview.isFrozen)

  const { showPlanningInfo, setShowPlanningInfo, canShowPlanningInfo } =
    useTeamEventPlanningInfoState(team.id, event)

  const {
    data: eventActivitiesPlanningInfo,
    isLoading: isPlanningInfoLoading,
  } = useEventActivitiesPlanningInfo(
    team.id,
    eventId,
    event?.activities || [],
    canAdminEdit || showPlanningInfo,
  )
  const applyEventUpdate = useApplyEventUpdate()
  const optimisticEventUpdate = useOptimisticEventUpdate(eventId)
  const {
    updateEventMutation,
    updateStatusMutation,
    updateAdminCommentMutation,
    updateSelectionMutation,
    removeGuestMutation,
    updateActivitySelectionForAllParticipantsMutation,
    updateSelectionForAllActivitiesMutation,
    updateEventActivityMutation,
    updateEventActivityOrderMutation,
    copyEventActivitySelectionsMutation,
    importEventActivityFromDefaultSetupMutation,
    exportEventActivityOverridesMutation,
    createEventActivitiesMutation,
    deleteEventActivityMutation,
    importEventFromPreviousEventMutation,
    resetEventSelectionsMutation,
    bulkUpdateActiveMutation,
  } = useTeamEventMutations({
    teamId: team.id,
    eventId,
    applyEventUpdate,
    optimisticEventUpdate,
    onError: (error) => {
      displayErrorToast(error as Error, t)
    },
  })
  const handleChannelEventUpdated = useCallback(
    (nextEvent: wowaudit.Event) => {
      if (preview.isFrozen) return
      optimisticEventUpdate.applyServerUpdate(nextEvent)
    },
    [optimisticEventUpdate, preview.isFrozen],
  )

  const updateContextValue: TeamEventUpdateContextValue = {
    isSaving:
      updateEventMutation.isPending ||
      updateStatusMutation.isPending ||
      updateAdminCommentMutation.isPending ||
      updateSelectionMutation.isPending ||
      removeGuestMutation.isPending ||
      updateActivitySelectionForAllParticipantsMutation.isPending ||
      updateSelectionForAllActivitiesMutation.isPending ||
      updateEventActivityMutation.isPending ||
      updateEventActivityOrderMutation.isPending ||
      copyEventActivitySelectionsMutation.isPending ||
      importEventActivityFromDefaultSetupMutation.isPending ||
      exportEventActivityOverridesMutation.isPending ||
      createEventActivitiesMutation.isPending ||
      deleteEventActivityMutation.isPending ||
      importEventFromPreviousEventMutation.isPending ||
      resetEventSelectionsMutation.isPending ||
      bulkUpdateActiveMutation.isPending,
    isPreviewActive: false,
    editEvent: async (values: AddEventFormValues) => {
      await updateEventMutation.mutateAsync(values)
    },
    updateEvent: async (values: Partial<wowaudit.Event>) => {
      await updateEventMutation.mutateAsync(values)
    },
    updateStatus: (
      eventParticipantId: number,
      status: wowaudit.EventParticipantReference['status'],
    ) => {
      updateStatusMutation.mutate({
        eventParticipantId,
        status,
      })
    },
    updateAdminComment: (eventParticipantId: number, adminComment: string) => {
      updateAdminCommentMutation.mutate({
        eventParticipantId,
        adminComment,
      })
    },
    updateSelection: (
      eventParticipantId: number,
      updates: {
        role?: string
        specId?: number | null
        characterId?: number
        selected?: boolean
      },
      activityId?: number,
    ) => {
      updateSelectionMutation.mutate({
        eventParticipantId,
        activityId,
        ...updates,
      })
    },
    updateActivitySelectionForAllParticipants: (
      activityId: number,
      selected: boolean,
    ) => {
      updateActivitySelectionForAllParticipantsMutation.mutate({
        activityId,
        selected,
      })
    },
    updateSelectionForAllActivities: (
      activityIds: number[],
      selected: boolean,
    ) => {
      updateSelectionForAllActivitiesMutation.mutate({
        activityIds,
        selected,
      })
    },
    updateEventActivity: (
      activityId: number,
      updates: UpdateEventActivityPayload,
      optimisticPlanOverrides?: wowaudit.EventActivity['planOverrides'],
    ) => {
      return updateEventActivityMutation
        .mutateAsync({
          activityId,
          eventActivity: updates,
          optimisticPlanOverrides,
        })
        .then(() => undefined)
    },
    updateEventActivityOrder: (activityOrder: number[]) => {
      return updateEventActivityOrderMutation
        .mutateAsync({ activityOrder })
        .then(() => undefined)
    },
    copySelectionsFromActivity: (
      activityId: number,
      sourceActivityId: number,
      sourceEventId?: number,
    ) => {
      return copyEventActivitySelectionsMutation
        .mutateAsync({ activityId, sourceActivityId, sourceEventId })
        .then(() => undefined)
    },
    importSelectionsFromDefaultSetup: (activityId: number) => {
      return importEventActivityFromDefaultSetupMutation
        .mutateAsync({ activityId })
        .then(() => undefined)
    },
    exportEventActivityOverrides: (activityId: number, raidPlanId: number) => {
      return exportEventActivityOverridesMutation
        .mutateAsync({ activityId, raidPlanId })
        .then(() => undefined)
    },
    addEventActivities: (eventActivity: CreateEventActivitiesPayload) => {
      return createEventActivitiesMutation
        .mutateAsync({ eventActivity })
        .then(() => undefined)
    },
    removeEventActivity: (activityId: number) => {
      return deleteEventActivityMutation
        .mutateAsync({ activityId })
        .then(() => undefined)
    },
    importEventFromPreviousEvent: (sourceEventId: number) => {
      return importEventFromPreviousEventMutation
        .mutateAsync({ sourceEventId })
        .then(() => undefined)
    },
    resetAllSelections: () => {
      return resetEventSelectionsMutation.mutateAsync().then(() => undefined)
    },
    setAllActivitiesActive: (active: boolean) => {
      return bulkUpdateActiveMutation.mutateAsync(active).then(() => undefined)
    },
    removeGuest: (eventParticipantId: number) => {
      removeGuestMutation.mutate(eventParticipantId)
    },
  }
  const previewUpdateContextValue: TeamEventUpdateContextValue = {
    isSaving: false,
    isPreviewActive: true,
    editEvent: async () => undefined,
    updateEvent: async () => undefined,
    updateStatus: (eventParticipantId, status) => {
      let previousStatus:
        | wowaudit.EventParticipantReference['status']
        | undefined
      preview.patchPreviewEvent((current) => {
        previousStatus = current.participants.find(
          (participant) => participant.id === eventParticipantId,
        )?.status
        return applyParticipantStatus(current, eventParticipantId, status)
      })
      void updateEventParticipantStatus(team.id, eventParticipantId, status)
        .then((serverEvent) => {
          const serverStatus = serverEvent.participants.find(
            (participant) => participant.id === eventParticipantId,
          )?.status
          if (!serverStatus) return
          preview.patchPreviewEvent((current) =>
            applyParticipantStatus(current, eventParticipantId, serverStatus),
          )
        })
        .catch((error) => {
          const statusToRestore = previousStatus
          if (statusToRestore) {
            preview.patchPreviewEvent((current) =>
              applyParticipantStatus(
                current,
                eventParticipantId,
                statusToRestore,
              ),
            )
          }
          displayErrorToast(error as Error, t)
        })
    },
    updateAdminComment: (eventParticipantId, adminComment) => {
      let previousComment: string | undefined
      preview.patchPreviewEvent((current) => {
        previousComment =
          current.participants.find(
            (participant) => participant.id === eventParticipantId,
          )?.adminComment ?? ''
        return applyParticipantAdminComment(
          current,
          eventParticipantId,
          adminComment,
        )
      })
      void updateEventParticipantAdminComment(
        team.id,
        eventParticipantId,
        adminComment,
      )
        .then((serverEvent) => {
          const serverComment =
            serverEvent.participants.find(
              (participant) => participant.id === eventParticipantId,
            )?.adminComment ?? ''
          preview.patchPreviewEvent((current) =>
            applyParticipantAdminComment(
              current,
              eventParticipantId,
              serverComment,
            ),
          )
        })
        .catch((error) => {
          const commentToRestore = previousComment
          if (commentToRestore !== undefined) {
            preview.patchPreviewEvent((current) =>
              applyParticipantAdminComment(
                current,
                eventParticipantId,
                commentToRestore,
              ),
            )
          }
          displayErrorToast(error as Error, t)
        })
    },
    updateSelection: (eventParticipantId, updates, activityId) => {
      preview.patchPreviewEvent((current) =>
        applySelection(
          current,
          { eventParticipantId, ...updates, activityId },
          team,
        ),
      )
    },
    updateActivitySelectionForAllParticipants: (activityId, selected) => {
      preview.patchPreviewEvent((current) =>
        applyActivitySelectionForAll(current, { activityId, selected }),
      )
    },
    updateSelectionForAllActivities: (activityIds, selected) => {
      preview.patchPreviewEvent((current) =>
        applyAllActivitiesSelection(current, { activityIds, selected }),
      )
    },
    updateEventActivity: (activityId, updates, optimisticPlanOverrides) => {
      const { difficulty: _difficulty, ...previewUpdates } = updates
      preview.patchPreviewEvent((current) =>
        applyActivityUpdate(current, {
          activityId,
          eventActivity: previewUpdates,
          optimisticPlanOverrides,
        }),
      )
      return Promise.resolve()
    },
    copySelectionsFromActivity: async () => undefined,
    importSelectionsFromDefaultSetup: async () => undefined,
    exportEventActivityOverrides: async () => undefined,
    updateEventActivityOrder: (activityOrder) => {
      preview.patchPreviewEvent((current) =>
        applyActivityOrder(current, { activityOrder }),
      )
      return Promise.resolve()
    },
    addEventActivities: async () => undefined,
    removeEventActivity: async () => undefined,
    importEventFromPreviousEvent: async () => undefined,
    resetAllSelections: async () => undefined,
    setAllActivitiesActive: async () => undefined,
    removeGuest: () => undefined,
  }
  const sortedActivities = useMemo(() => {
    if (!event?.activities?.length) return []

    return event.activities
      .slice()
      .sort((left, right) => left.order - right.order)
  }, [event?.activities])

  const initialEventReference = getReference<wowaudit.EventReference>(
    `${team.path}/events/${eventId}`,
  )

  if (!isLoadingEvent && (hasEventError || !event)) {
    return <Navigate to={`${team.path}/events`} replace />
  }

  if (isLoadingEvent) {
    return (
      <Section title={`Event | ${team.name} | ${guild.name}`}>
        <TeamEventSkeleton eventReference={initialEventReference} />
      </Section>
    )
  }

  const skipGetStartedBanner =
    !!event &&
    gameVersion.has('flexible_raid_size') &&
    event.primaryDifficulty != null &&
    event.primaryDifficulty !== 'mythic' &&
    event.participants.length < 30

  return (
    <Section title={`Event | ${team.name} | ${guild.name}`}>
      <TeamEventUpdateProvider
        value={
          preview.isActive ? previewUpdateContextValue : updateContextValue
        }
      >
        <TeamEventPlanningInfoProvider
          value={{ showPlanningInfo, setShowPlanningInfo }}
        >
          <EventHeader
            eventId={eventId}
            initialEventReference={initialEventReference}
            onAddParticipants={() => setAddParticipantsOpen(true)}
            onAddActivities={() => setAddActivitiesOpen(true)}
            onEventUpdated={handleChannelEventUpdated}
            previewActive={preview.isFrozen}
          />

          {event ? (
            <TeamEventParticipantsProvider
              event={event}
              activities={sortedActivities}
              planningInfo={eventActivitiesPlanningInfo}
            >
              <TeamEventLinkedPlanDataProvider
                activities={sortedActivities}
                activityType={event.activityType}
              >
                {({ linkedPlanDataByActivityId, isRaidPlansLoading }) => {
                  const scopedActivity =
                    resolveEventFocusActivity(event, sortedActivities) ??
                    sortedActivities[0]
                  const primaryActivity = sortedActivities.find(
                    (activity) => activity.id === event.primaryActivityId,
                  )
                  const isNarrowLayout =
                    sortedActivities.length > 0 &&
                    event.activityType !== 'raids'

                  return (
                    <>
                      <div
                        className={cn(isNarrowLayout && 'max-w-5xl mx-auto')}
                      >
                        <ViewerSignupCard
                          event={event}
                          activities={sortedActivities}
                          linkedPlanDataByActivityId={
                            linkedPlanDataByActivityId
                          }
                          isRaidPlansLoading={isRaidPlansLoading}
                        />

                        <EventNotes
                          event={event}
                          canEdit={canAdminEdit && !preview.isActive}
                          isEditing={isEditingNotes}
                          onStartEdit={() => setIsEditingNotes(true)}
                          onStopEdit={() => setIsEditingNotes(false)}
                        />
                      </div>

                      {canAdminEdit &&
                      !event.touched &&
                      !preview.isFrozen &&
                      (team.members ?? []).length > 0 &&
                      event.activityType === 'raids' &&
                      !skipGetStartedBanner ? (
                        <EventGetStartedBanner
                          event={event}
                          onImportClick={() => setIsImportDialogOpen(true)}
                          onAutoSetupClick={() => setIsAutoSetupOpen(true)}
                        />
                      ) : null}

                      <div
                        className={cn(
                          'mt-4 mb-2 flex justify-between items-end flex-wrap gap-2 max-sm:flex-col max-sm:items-stretch',
                          isNarrowLayout && 'max-w-5xl mx-auto',
                        )}
                      >
                        <div className='flex flex-col gap-1'>
                          <EventNotesControl
                            event={event}
                            canEdit={canAdminEdit && !preview.isActive}
                            isEditing={isEditingNotes}
                            onStartEdit={() => setIsEditingNotes(true)}
                          />
                          <div
                            className={cn('flex items-stretch gap-2 flex-wrap')}
                          >
                            {event ? (
                              <EventAttendanceSummary event={event} />
                            ) : null}
                          </div>
                        </div>

                        <div className='flex flex-col items-end gap-1 max-sm:items-stretch'>
                          {event.activityType === 'raids' &&
                          canAdminEdit &&
                          (event.touched || skipGetStartedBanner) &&
                          sortedActivities.length > 1 ? (
                            <div
                              className={cn(
                                preview.isFrozen &&
                                  'invisible pointer-events-none',
                              )}
                              aria-hidden={preview.isFrozen}
                            >
                              <EventSetupModeControl event={event} />
                            </div>
                          ) : null}
                          <div className='flex items-end gap-2 flex-wrap'>
                            {event.raidsPlanningScope === 'single' &&
                            event.primaryActivityId != null ? null : (
                              <CopyAllNotesButton
                                activities={sortedActivities}
                                linkedPlanDataByActivityId={
                                  linkedPlanDataByActivityId
                                }
                                includeInactive={
                                  event.raidsPlanningScope === 'single'
                                }
                                isRaidPlansLoading={isRaidPlansLoading}
                              />
                            )}

                            {event.activityType === 'raids' && canAdminEdit ? (
                              <>
                                {canShowPlanningInfo ? (
                                  <Card className='flex items-center gap-2 h-9 px-2'>
                                    <Switch
                                      id='show-planning-info'
                                      checked={showPlanningInfo}
                                      onCheckedChange={(checked) =>
                                        setShowPlanningInfo(checked === true)
                                      }
                                    />
                                    <label
                                      htmlFor='show-planning-info'
                                      className='cursor-pointer select-none'
                                    >
                                      Planning info
                                    </label>
                                  </Card>
                                ) : null}
                                {event.raidsPlanningScope === 'per_encounter' ||
                                skipGetStartedBanner ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant='outline'
                                        size='sm'
                                        className='text-info-foreground border-info-foreground/50 hover:text-info-foreground'
                                        onClick={() => {
                                          if (
                                            event.raidsPlanningScope !==
                                            'per_encounter'
                                          ) {
                                            void updateContextValue.updateEvent(
                                              {
                                                touched: true,
                                                raidsPlanningScope:
                                                  'per_encounter',
                                              },
                                            )
                                          }
                                          setIsAutoSetupOpen(true)
                                        }}
                                      >
                                        <Bot className='h-4 w-4' />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {preview.isActive
                                        ? 'Back to config'
                                        : 'Auto setup'}
                                    </TooltipContent>
                                  </Tooltip>
                                ) : null}
                                <DropdownMenu modal={false}>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant='outline'
                                      size='sm'
                                      disabled={preview.isFrozen}
                                    >
                                      <Settings2Icon className='h-4 w-4' />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align='end'>
                                    <EventBulkSelectionMenuItem />
                                    <DropdownMenuItem
                                      onSelect={() => {
                                        setIsImportDialogOpen(true)
                                      }}
                                    >
                                      Import from previous event
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onSelect={() => {
                                        setIsResetSelectionsConfirmOpen(true)
                                      }}
                                    >
                                      Reset all selections
                                    </DropdownMenuItem>
                                    {event.raidsPlanningScope !== 'single' && (
                                      <DropdownMenuItem
                                        onSelect={() => {
                                          const anyActive =
                                            sortedActivities.some(
                                              (a) => a.active,
                                            )
                                          void updateContextValue.setAllActivitiesActive(
                                            !anyActive,
                                          )
                                        }}
                                      >
                                        {`Set all ${
                                          eventActivitiesNoun(sortedActivities)
                                            .plural
                                        } ${
                                          sortedActivities.some((a) => a.active)
                                            ? 'inactive'
                                            : 'active'
                                        }`}
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div
                        className='relative'
                        style={{ overflowAnchor: 'none' }}
                      >
                        <div
                          className={cn(
                            preview.isActive &&
                              'rounded-lg ring-2 ring-info-foreground',
                          )}
                        >
                          {sortedActivities.length === 0 ? (
                            <Card className='flex flex-col items-center gap-3 mt-2'>
                              <div className='text-sm text-muted-foreground'>
                                No activities found
                              </div>
                              {canAdminEdit && !preview.isActive ? (
                                <Button
                                  size='sm'
                                  onClick={() => setAddActivitiesOpen(true)}
                                >
                                  Add activity
                                </Button>
                              ) : null}
                            </Card>
                          ) : event.activityType !== 'raids' ? (
                            <EventActivityBlock
                              activity={sortedActivities[0]}
                            />
                          ) : event.raidsPlanningScope === 'per_encounter' ? (
                            <EventActivityGrid
                              activities={sortedActivities}
                              linkedPlanDataByActivityId={
                                linkedPlanDataByActivityId
                              }
                              isRaidPlansLoading={isRaidPlansLoading}
                              isPlanningInfoLoading={isPlanningInfoLoading}
                              resetToGridViewKey={resetGridViewKey}
                              previewControls={
                                preview.isActive
                                  ? {
                                      isCommitting: preview.isCommitting,
                                      isGenerating: preview.isGenerating,
                                      isDiscarding: preview.isDiscarding,
                                      onDiscard: preview.discard,
                                      onCommit: () => void preview.commit(),
                                    }
                                  : undefined
                              }
                            />
                          ) : primaryActivity ? (
                            <EventActivitySpecificEncounter
                              activity={primaryActivity}
                              activities={sortedActivities}
                              linkedPlanData={linkedPlanDataByActivityId.get(
                                primaryActivity.id,
                              )}
                              isLinkedPlanDataLoading={isRaidPlansLoading}
                              isPlanningInfoLoading={isPlanningInfoLoading}
                            />
                          ) : (
                            <EventActivitySpecificEncounter
                              activity={scopedActivity}
                              activities={sortedActivities}
                              linkedPlanDataByActivityId={
                                linkedPlanDataByActivityId
                              }
                              isLinkedPlanDataLoading={isRaidPlansLoading}
                              isPlanningInfoLoading={isPlanningInfoLoading}
                              allEncounters
                            />
                          )}
                        </div>
                        {preview.isGenerating ? (
                          <AutoSetupGeneratingOverlay
                            stickyTopPx={stickyTopPx}
                          />
                        ) : null}
                      </div>
                    </>
                  )
                }}
              </TeamEventLinkedPlanDataProvider>

              <AddParticipantModal
                open={addParticipantsOpen}
                setOpen={setAddParticipantsOpen}
                event={event}
              />
              <AddEventActivitiesDialog
                open={addActivitiesOpen}
                setOpen={setAddActivitiesOpen}
                event={event}
                isPending={createEventActivitiesMutation.isPending}
                onSubmit={async (payload) => {
                  await updateContextValue.addEventActivities(payload)
                }}
              />
              {canAdminEdit ? (
                <>
                  <ImportFromPreviousEventDialog
                    event={event}
                    open={isImportDialogOpen}
                    onOpenChange={setIsImportDialogOpen}
                    onImport={async (sourceEventId) => {
                      await updateContextValue.importEventFromPreviousEvent(
                        sourceEventId,
                      )
                    }}
                  />
                  <AutoSetupDialog
                    event={event}
                    activities={sortedActivities}
                    open={isAutoSetupOpen}
                    onOpenChange={setIsAutoSetupOpen}
                    initialActivityIds={preview.lastActivityIds}
                    onGenerate={preview.generate}
                    isPreviewActive={preview.isActive}
                    onDiscard={preview.discard}
                  />
                  <ConfirmDialog
                    open={isResetSelectionsConfirmOpen}
                    title='Reset all selections?'
                    description='This will set all encounter selections back to the default value for all participants in this event.'
                    variant='destructive'
                    onCancel={() => setIsResetSelectionsConfirmOpen(false)}
                    onContinue={async () => {
                      await updateContextValue.resetAllSelections()
                      setIsResetSelectionsConfirmOpen(false)
                    }}
                  />
                </>
              ) : null}
            </TeamEventParticipantsProvider>
          ) : null}
        </TeamEventPlanningInfoProvider>
      </TeamEventUpdateProvider>
      <AutoSetupPreviewBar
        open={preview.isActive}
        isGenerating={preview.isGenerating}
        isDiscarding={preview.isDiscarding}
        isCommitting={preview.isCommitting}
        onBackToConfig={preview.discardAndConfigure}
        onRegenerate={preview.regenerate}
        onDiscard={preview.discard}
        onCommit={() => void preview.commit()}
      />
    </Section>
  )
}

export default TeamEvent
